/**
 * Purview Data Governance Service
 * All calls go through internal API — frontend NEVER calls Microsoft Purview directly.
 */
import { AZURE } from './api';

// ─── Aba 1: Visão Geral ────────────────────────────────────────────────────
export const getPurviewOverview = async () => {
  const { data } = await AZURE.get('/governance/purview/overview');
  return data;
};

// ─── Aba 2: Fontes de Dados ────────────────────────────────────────────────
export const listPurviewSources = async () => {
  const { data } = await AZURE.get('/governance/purview/sources');
  return data;
};

// ─── Aba 3: Catálogo de Assets ─────────────────────────────────────────────
export const listPurviewAssets = async (filters = {}) => {
  const { data } = await AZURE.get('/governance/purview/assets', { params: filters });
  return data;
};

export const getAssetDetails = async (assetId) => {
  const { data } = await AZURE.get(`/governance/purview/assets/${assetId}`);
  return data;
};

// ─── Aba 5: Linhagem ───────────────────────────────────────────────────────
export const getAssetLineage = async (assetId) => {
  const { data } = await AZURE.get(`/governance/purview/assets/${assetId}/lineage`);
  return data;
};

// ─── Aba 4: Classificações ─────────────────────────────────────────────────
export const listClassifications = async () => {
  const { data } = await AZURE.get('/governance/purview/classifications');
  return data;
};

export const getClassificationAssets = async (classificationName) => {
  const { data } = await AZURE.get(`/governance/purview/classifications/${encodeURIComponent(classificationName)}/assets`);
  return data;
};

export const updateAssetClassification = async (assetId, payload) => {
  const { data } = await AZURE.patch(`/governance/purview/assets/${assetId}/classification`, payload);
  return data;
};

// ─── Aba 6: Qualidade ──────────────────────────────────────────────────────
export const getPurviewQuality = async () => {
  const { data } = await AZURE.get('/governance/purview/quality');
  return data;
};

export const getQualityDetail = async (metric) => {
  const { data } = await AZURE.get(`/governance/purview/quality/${encodeURIComponent(metric)}/assets`);
  return data;
};

// ─── Aba 7: Políticas ──────────────────────────────────────────────────────
export const listPolicies = async () => {
  const { data } = await AZURE.get('/governance/purview/policies');
  return data;
};

export const updatePolicy = async (policyId, payload) => {
  const { data } = await AZURE.patch(`/governance/purview/policies/${policyId}`, payload);
  return data;
};

export const createPolicy = async (payload) => {
  const { data } = await AZURE.post('/governance/purview/policies', payload);
  return data;
};

// ─── Aba 8: Glossário ──────────────────────────────────────────────────────
export const getPurviewGlossary = async () => {
  const { data } = await AZURE.get('/governance/purview/glossary');
  return data;
};

export const syncGlossary = async () => {
  const { data } = await AZURE.post('/governance/purview/glossary/sync');
  return data;
};

export const addGlossaryTerm = async (payload) => {
  const { data } = await AZURE.post('/governance/purview/glossary', payload);
  return data;
};

// ─── Aba 9: Scans ──────────────────────────────────────────────────────────
export const listScans = async () => {
  const { data } = await AZURE.get('/governance/purview/scans');
  return data;
};

export const runScan = async (sourceId) => {
  const { data } = await AZURE.post(`/governance/purview/scans/${sourceId}/run`);
  return data;
};

export const getScanHistory = async () => {
  const { data } = await AZURE.get('/governance/purview/scans/history');
  return data;
};

// ─── Asset Inline Edit ─────────────────────────────────────────────────────
export const updateAsset = async (assetId, payload) => {
  const { data } = await AZURE.patch(`/governance/purview/assets/${assetId}`, payload);
  return data;
};

// ─── Aba 10: Alertas ───────────────────────────────────────────────────────
export const listAlerts = async (includeResolved = false) => {
  const { data } = await AZURE.get('/governance/purview/alerts', { params: { resolved: includeResolved } });
  return data;
};
export const updateAlertStatus = async (alertId, statusOrPayload) => {
  const payload = typeof statusOrPayload === 'string'
    ? { status: statusOrPayload }
    : (statusOrPayload || {});
  const { data } = await AZURE.patch(`/governance/purview/alerts/${alertId}`, payload);
  return data;
};

export const getAlertDetail = async (alertId) => {
  const { data } = await AZURE.get(`/governance/purview/alerts/${alertId}`);
  return data;
};

export const getPurviewCosts = async () => {
  const { data } = await AZURE.get('/governance/purview/costs');
  return data;
};

// ─── Aba 11: Auditoria ─────────────────────────────────────────────────────
export const listAuditEvents = async () => {
  const { data } = await AZURE.get('/governance/purview/audit');
  return data;
};

// ─── Aba 12: Configurações ─────────────────────────────────────────────────
export const getPurviewSettings = async () => {
  const { data } = await AZURE.get('/governance/purview/settings');
  return data;
};

export const updatePurviewSettings = async (payload) => {
  const { data } = await AZURE.patch('/governance/purview/settings', payload);
  return data;
};

// ─── Bulk Resolve Alerts ───────────────────────────────────────────────────
export const bulkResolveAlerts = async () => {
  const { data } = await AZURE.post('/governance/purview/alerts/bulk-resolve');
  return data;
};

// ─── Quality Auto-Fix ──────────────────────────────────────────────────────
export const qualityAutoFix = async (action = 'all', dryRun = false) => {
  const { data } = await AZURE.post('/governance/purview/quality/auto-fix', { action, dry_run: dryRun });
  return data;
};

export const fixQualityMetric = async (metric, payload) => {
  const { data } = await AZURE.post(`/governance/purview/quality/${encodeURIComponent(metric)}/fix`, payload);
  return data;
};

// ─── Purview AI Agent ──────────────────────────────────────────────────────
export const chatPurviewAgent = async (message, history = []) => {
  const { data } = await AZURE.post('/governance/purview/agent/chat', { message, history });
  return data;
};
